import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader, Surface, Btn, Field, Input } from "../components/Primitives";
import { ArrowLeft, CheckCircle, X, FileText, UploadSimple, Trash } from "@phosphor-icons/react";

/**
 * Phase 6A — Superuser aircraft type detail page.
 * - Edit type data
 * - Approve / reject (if pending)
 * - Upload general docs (≤300MB) that flow into the RAG type library
 */
export default function SuperuserTypeDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [t, setT] = useState(null);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [uploadKind, setUploadKind] = useState("manual");
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/superuser/aircraft-types/${slug}`);
      setT(r.data);
      const dr = await api.get(`/superuser/aircraft-types/${slug}/docs`);
      setDocs(dr.data || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Load failed");
    }
  }, [slug]);

  useEffect(() => {
    if (authLoading) return;
    if (!user?.platform_superuser) {
      navigate("/", { replace: true });
      return;
    }
    load();
  }, [load, user, authLoading, navigate]);

  const patch = async (update) => {
    setBusy(true); setErr(null);
    try {
      const r = await api.patch(`/superuser/aircraft-types/${slug}`, update);
      setT(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/superuser/aircraft-types/${slug}/approve`, {});
      setT(r.data);
    } finally { setBusy(false); }
  };
  const reject = async () => {
    const notes = window.prompt("Reason for rejection (optional):") || "";
    setBusy(true);
    try {
      const r = await api.post(`/superuser/aircraft-types/${slug}/reject`, { notes });
      setT(r.data);
    } finally { setBusy(false); }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true); setErr(null);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("kind", uploadKind);
    if (uploadTitle) fd.append("title", uploadTitle);
    try {
      await api.post(`/superuser/aircraft-types/${slug}/docs`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      setUploadTitle("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      setErr(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deleteDoc = async (mid) => {
    if (!window.confirm("Delete this document and remove from chat library?")) return;
    await api.delete(`/superuser/aircraft-types/${slug}/docs/${mid}`);
    await load();
  };

  if (!t) return null;

  return (
    <div>
      <div className="px-8 pt-8">
        <Link to="/superuser" className="text-xs text-zinc-400 hover:text-amber inline-flex items-center gap-1.5">
          <ArrowLeft size={14} /> Back to superuser
        </Link>
      </div>
      <PageHeader
        overline="Aircraft type"
        title={`${t.manufacturer} ${t.model}`}
        subtitle={<>Slug <span className="font-mono text-xs">{t.slug}</span> · Status <span className={t.status === "approved" ? "text-emerald-300" : t.status === "pending_review" ? "text-amber" : "text-red-300"}>{t.status}</span></>}
        actions={
          t.status === "pending_review" && (
            <div className="flex gap-2">
              <Btn variant="ghost" onClick={reject} disabled={busy} data-testid="type-reject-btn">
                <X size={14} className="inline mr-1" /> Reject
              </Btn>
              <Btn onClick={approve} disabled={busy} data-testid="type-approve-btn">
                <CheckCircle size={14} className="inline mr-1" /> Approve
              </Btn>
            </div>
          )
        }
      />

      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Edit core data */}
        <Surface className="p-6 space-y-4" data-testid="type-edit-card">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Type data</div>
          {[
            ["manufacturer", "Manufacturer", "text"],
            ["model", "Model", "text"],
            ["category", "Category", "text"],
            ["seats", "Seats", "number"],
            ["fuel_type", "Fuel type", "text"],
            ["fuel_capacity_l", "Fuel capacity (L)", "number"],
            ["default_max_members", "Default max members", "number"],
            ["default_currency", "Default currency", "text"],
          ].map(([k, label, type]) => (
            <Field key={k} label={label}>
              <Input
                type={type}
                value={t[k] ?? ""}
                onChange={(e) => setT({ ...t, [k]: type === "number" ? parseFloat(e.target.value || 0) : e.target.value })}
                onBlur={() => patch({ [k]: t[k] })}
                disabled={busy}
                data-testid={`type-input-${k}`}
              />
            </Field>
          ))}
          <Field label="Engine">
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="Manufacturer" value={t.engine?.manufacturer || ""}
                     onChange={(e) => setT({ ...t, engine: { ...(t.engine||{}), manufacturer: e.target.value }})}
                     onBlur={() => patch({ engine: t.engine })} disabled={busy} />
              <Input placeholder="Model" value={t.engine?.model || ""}
                     onChange={(e) => setT({ ...t, engine: { ...(t.engine||{}), model: e.target.value }})}
                     onBlur={() => patch({ engine: t.engine })} disabled={busy} />
              <Input type="number" placeholder="TBO h" value={t.engine?.tbo_hours || ""}
                     onChange={(e) => setT({ ...t, engine: { ...(t.engine||{}), tbo_hours: parseFloat(e.target.value || 0) }})}
                     onBlur={() => patch({ engine: t.engine })} disabled={busy} />
            </div>
          </Field>
          {t.submitted_by_email && (
            <div className="pt-3 border-t border-zinc-800 text-[11px] font-mono text-zinc-500">
              Submitted by {t.submitted_by_email}
              {t.review_notes && <div className="mt-1 text-zinc-400">Review notes: {t.review_notes}</div>}
            </div>
          )}
        </Surface>

        {/* General docs upload */}
        <Surface className="p-6 space-y-4" data-testid="type-docs-card">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono flex items-center gap-2">
            <FileText size={12} /> General documents (≤ 300MB PDF)
          </div>
          <div className="p-3 border border-amber/30 bg-amber/5 text-[11px] text-amber/90 font-mono">
            Docs uploaded here are RAG-indexed into the shared type library
            and become searchable from the AI Assistant chat for EVERY workspace
            operating this type.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Document kind">
              <select value={uploadKind} onChange={(e) => setUploadKind(e.target.value)}
                      className="w-full h-10 bg-[#0F0F0F] border border-zinc-800 px-3 text-sm font-mono">
                <option value="poh">POH</option>
                <option value="manual">Maintenance manual</option>
                <option value="parts_catalog">Parts catalogue</option>
                <option value="service_bulletin">Service bulletin / AD</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="Title (optional)">
              <Input value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} />
            </Field>
          </div>
          <input ref={fileRef} type="file" accept="application/pdf" onChange={onUpload}
                 className="w-full text-xs text-zinc-400 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-amber file:text-black file:font-mono file:text-[10px] file:uppercase file:tracking-wider hover:file:bg-amber/90"
                 data-testid="type-doc-upload-input" />
          {uploading && (
            <div className="text-xs text-amber font-mono" data-testid="type-doc-uploading">
              Uploading + ingesting (background)…
            </div>
          )}

          <div className="pt-3 border-t border-zinc-800 space-y-2 max-h-72 overflow-y-auto"
               data-testid="type-docs-list">
            {docs.length === 0 ? (
              <div className="text-xs text-zinc-500 font-mono text-center py-4">No general documents uploaded yet.</div>
            ) : docs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 text-xs py-2 border-b border-zinc-900 last:border-b-0">
                <FileText size={14} className="text-zinc-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="truncate">{d.title}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">
                    {d.kind} · {(d.size_bytes / 1024 / 1024).toFixed(1)} MB · {d.status} · {d.chunk_count || 0} chunks
                  </div>
                </div>
                <button onClick={() => deleteDoc(d.id)} className="text-zinc-500 hover:text-red-400"
                        data-testid={`delete-doc-${d.id}`}>
                  <Trash size={14} />
                </button>
              </div>
            ))}
          </div>
        </Surface>

        {err && (
          <div className="lg:col-span-2 p-3 border border-red-900/60 bg-red-950/20 text-xs text-red-300 font-mono" data-testid="type-error">
            {err}
          </div>
        )}
      </div>
    </div>
  );
}
